import React, { useSyncExternalStore } from 'react';
import { Button, Dialog } from '@allygpo/ally-ux';

export interface ConfirmOptions {
  header?: string;
  message: React.ReactNode;
  /** PrimeIcons class shown beside the message, e.g. 'pi pi-exclamation-triangle'. */
  icon?: string;
  /** Defaults to "Yes". */
  acceptLabel?: string;
  /** Defaults to "No". */
  rejectLabel?: string;
  /** Destructive action: the accept button uses the danger style. */
  danger?: boolean;
  accept?: () => void;
  /** Called only from the reject button; Esc, the close icon or the mask just close it. */
  reject?: () => void;
}

// One confirmation at a time, shown by the <ConfirmHost /> in Layout (Ally UX has no ConfirmDialog).
let current: ConfirmOptions | null = null;
let visible = false;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((listener) => listener());
const subscribe = (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; };

/** Ask for confirmation. Drop-in for PrimeReact's confirmDialog() with the options ScenarioIQ used. */
export const confirm = (options: ConfirmOptions) => {
  current = options;
  visible = true;
  emit();
};

const close = () => {
  visible = false;
  emit();
};

export const ConfirmHost: React.FC = () => {
  const isVisible = useSyncExternalStore(subscribe, () => visible);
  // The last options stay rendered while the dialog animates out.
  const options = useSyncExternalStore(subscribe, () => current);
  if (!options) return null;
  return (
    <Dialog
      visible={isVisible}
      onHide={close}
      header={options.header}
      size="sm"
      footer={<div className="flex justify-content-end u-gap-2">
        <Button variant="secondary-text" onClick={() => { close(); options.reject?.(); }}>{options.rejectLabel ?? 'No'}</Button>
        <Button variant={options.danger ? 'danger' : 'primary'} onClick={() => { close(); options.accept?.(); }}>{options.acceptLabel ?? 'Yes'}</Button>
      </div>}
    >
      <div className="flex align-items-start u-gap-4">
        {options.icon && <i className={options.icon} aria-hidden="true" />}
        <div>{options.message}</div>
      </div>
    </Dialog>
  );
};
